import { useCallback, useEffect, useState } from "react";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext.tsx";
import { useAzureBlobUpload } from "@features/port-security/hooks/useAzureBlobUpload.tsx";
import type { MovementOutletContext } from "@features/vessel-call/vessel-call-status/MovementServices/MovementServicesContainer";
import { useMovementServiceFormStore } from "@features/vessel-call/vessel-call-status/MovementServices/store/useMovementServiceForm.store.ts";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types.ts";
import { getSasToken } from "@services/userApi.ts";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import type { MovementServiceFormData } from "../schema/movementServiceFormSchema";

export type MovementDocumentField = "additionalDocuments" | "pilotRequestNotification" | "h2s";

type MovementDocumentFormEntry = NonNullable<
  MovementServiceFormData["additionalDocuments"]
>[number];

export type UploadItem = Pick<
  MovementDocumentFormEntry,
  "name" | "url" | "certificateType" | "idx"
> & {
  type?: string;
  documentType?: string;
  otherSpecifyType?: string;
  documentName?: string;
  issueDate?: Date | string | null;
  expiryDate?: Date | string | null;
  documentSummary?: string;
  maxFiles?: number;
};

const DEFAULT_UPLOAD_CONTENT_TYPE = "application/octet-stream";

const emptyAdditionalDocumentMetadata = {
  documentType: "",
  otherSpecifyType: "",
  documentName: "",
  issueDate: null,
  expiryDate: null,
  documentSummary: "",
};

const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

const hasDateValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const pickNonEmpty = <T,>(...values: Array<T | undefined | null>): T | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      if (value.trim().length > 0) return value as T;
      continue;
    }

    if (value != null) return value as T;
  }

  return undefined;
};

const pickDateOrNull = (
  primary: UploadItem["issueDate"] | UploadItem["expiryDate"],
  fallback: UploadItem["issueDate"] | UploadItem["expiryDate"],
) => {
  if (hasDateValue(primary)) return primary;
  if (hasDateValue(fallback)) return fallback;
  return null;
};

interface UseMovementDocumentUploadOptions {
  fieldName: MovementDocumentField;
  certificateType: string;
  formData: MovementOutletContext["formData"];
  updateFormData: MovementOutletContext["updateFormData"];
}

interface UseMovementDocumentUploadResult {
  files: UploadEntry[];
  isLoading?: boolean;
  setFiles: (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => void;
  handleFieldChangeForAzure: (fieldName: string, files: File[]) => Promise<void>;
  removeFileAtIndex: (index: number) => void;
}

export const useMovementDocumentUpload = ({
  fieldName,
  certificateType,
  formData,
  updateFormData,
}: UseMovementDocumentUploadOptions): UseMovementDocumentUploadResult => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);
  const files = useUploadStore((state) => state.filesByKey[fieldName] ?? EMPTY_UPLOAD_ENTRIES);

  const updateStoreFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => {
      if (typeof next === "function") {
        updateFilesForKey(fieldName, next as (prev: UploadEntry[]) => UploadEntry[]);
      } else {
        setFilesForKey(fieldName, next);
      }
    },
    [fieldName, setFilesForKey, updateFilesForKey],
  );

  useEffect(() => {
    const storeState = useUploadStore.getState();
    const movementServiceState = useMovementServiceFormStore.getState();
    const storedEntries = movementServiceState[fieldName] as UploadEntry[];

    if (
      Array.isArray(storedEntries) &&
      storedEntries.length &&
      storeState.getFilesForKey(fieldName).length === 0
    ) {
      storeState.setFilesForKey(fieldName, storedEntries);
    }
  }, [fieldName]);

  const [pendingValue, setPendingValue] = useState<UploadItem[] | null>(null);
  const [uploadsInFlight, setUploadsInFlight] = useState(0);

  const persistFormEntries = useCallback(
    (entries: UploadItem[]) => {
      formData.setValue(fieldName, entries, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      setPendingValue(entries);
    },
    [fieldName, formData],
  );

  useEffect(() => {
    if (pendingValue) {
      updateFormData(fieldName, pendingValue);
      setPendingValue(null);
    }
  }, [fieldName, pendingValue, updateFormData]);

  const updateArray = useCallback(
    (item: { documentUrl: string; idx?: number }) => {
      const currentVal = formData.getValues(fieldName);
      const currentState: UploadItem[] = Array.isArray(currentVal) ? currentVal : [];

      const { documentUrl } = item;
      const idxFromResponse = Number.isInteger(item.idx) ? (item.idx as number) : -1;
      const fallbackIdx = currentState.findIndex(
        (it) => !it?.url && it?.certificateType === certificateType,
      );
      const targetIdx =
        idxFromResponse >= 0
          ? idxFromResponse
          : fallbackIdx >= 0
            ? fallbackIdx
            : currentState.length;

      const next = [...currentState];
      const prev = next[targetIdx];
      next[targetIdx] = {
        ...prev,
        ...(fieldName === "additionalDocuments" && !prev?.url
          ? emptyAdditionalDocumentMetadata
          : {}),
        name: prev?.name ?? "",
        url: documentUrl,
        certificateType,
        type: prev?.type,
        idx: targetIdx,
      };

      persistFormEntries(next);
      updateStoreFiles((currentEntries) => {
        const updatedEntries = [...currentEntries];

        if (targetIdx < updatedEntries.length) {
          updatedEntries[targetIdx] = next[targetIdx];
        } else if (next[targetIdx]) {
          updatedEntries.push(next[targetIdx]);
        }

        return updatedEntries;
      });
    },
    [certificateType, fieldName, formData, persistFormEntries, updateStoreFiles],
  );

  const handleUploadError = useCallback(
    (error: Error) => {
      notify({
        title: t("security-clearance.notifications.sc-request-upload-error-title"),
        description: t("security-clearance.notifications.sc-request-upload-error-description"),
        type: "error",
      });
      console.error("Error on upload documents:", error);
    },
    [notify, t],
  );

  const handleUploadSuccess = useCallback(
    (
      dataArray: { documentUrl: string; certificateType: string }[],
      context: { certificateType: string; storeKey: string; idx?: number },
    ) => {
      if (!Array.isArray(dataArray)) return;
      const baseIdx = typeof context.idx === "number" ? context.idx : 0;
      dataArray.forEach((data, responseIdx) => {
        updateArray({ documentUrl: data.documentUrl, idx: baseIdx + responseIdx });
      });
    },
    [updateArray],
  );

  const uploadDocumentsToAzure = useAzureBlobUpload({
    handleUploadSuccess,
    handleUploadError,
  });

  const buildFormItemsFromStore = useCallback((): UploadItem[] => {
    const storeEntries =
      useUploadStore.getState().getFilesForKey(fieldName) ?? EMPTY_UPLOAD_ENTRIES;
    const formStateEntriesRaw = formData.getValues(fieldName);
    const formStateEntries = Array.isArray(formStateEntriesRaw) ? formStateEntriesRaw : [];
    const persistedEntriesRaw = useMovementServiceFormStore.getState()[fieldName] as
      | UploadItem[]
      | undefined;
    const persistedEntries = Array.isArray(persistedEntriesRaw) ? persistedEntriesRaw : [];

    const findByIdentity = (
      entries: UploadItem[],
      candidate: Partial<UploadItem>,
    ): UploadItem | undefined => {
      if (candidate.url) {
        const byUrl = entries.find((entry) => entry.url === candidate.url);
        if (byUrl) return byUrl;
      }

      if (candidate.name) {
        const byName = entries.find((entry) => entry.name === candidate.name);
        if (byName) return byName;
      }

      return undefined;
    };

    const getPreviousEntry = (
      candidate: Partial<UploadItem>,
      idx: number,
    ): UploadItem | undefined => {
      const formIdentityMatch = findByIdentity(formStateEntries, candidate);
      if (formIdentityMatch) return formIdentityMatch;

      const persistedIdentityMatch = findByIdentity(persistedEntries, candidate);
      if (persistedIdentityMatch) return persistedIdentityMatch;

      const fromFormByIndex = formStateEntries[idx];
      if (fromFormByIndex && !(fromFormByIndex instanceof File)) {
        return fromFormByIndex as UploadItem;
      }

      const fromPersistedByIndex = persistedEntries[idx];
      if (fromPersistedByIndex && !(fromPersistedByIndex instanceof File)) {
        return fromPersistedByIndex as UploadItem;
      }

      return undefined;
    };

    return storeEntries.map((entry, idx) => {
      const previousEntry =
        entry instanceof File
          ? getPreviousEntry({ name: entry.name }, idx)
          : getPreviousEntry(entry as UploadItem, idx);

      if (entry instanceof File) {
        return {
          ...previousEntry,
          ...(fieldName === "additionalDocuments" && !previousEntry?.url
            ? emptyAdditionalDocumentMetadata
            : {}),
          name: entry.name,
          certificateType,
          type: entry.type,
          url: previousEntry?.url ?? "",
          idx,
        };
      }

      const typedEntry = entry as UploadItem & { type?: string };
      const resolvedCertificateType =
        pickNonEmpty(typedEntry.certificateType, previousEntry?.certificateType, typedEntry.type) ??
        certificateType;
      const resolvedIdx = pickNonEmpty<number>(typedEntry.idx, previousEntry?.idx, idx) ?? idx;
      const documentType = pickNonEmpty(typedEntry.documentType, previousEntry?.documentType) ?? "";

      return {
        ...previousEntry,
        ...typedEntry,
        name: pickNonEmpty(typedEntry.name, previousEntry?.name) ?? "",
        certificateType: resolvedCertificateType,
        type: pickNonEmpty(typedEntry.type, previousEntry?.type),
        url: pickNonEmpty(typedEntry.url, previousEntry?.url) ?? "",
        idx: resolvedIdx,
        documentType,
        otherSpecifyType:
          documentType === "OTHER_SPECIFY"
            ? (pickNonEmpty(typedEntry.otherSpecifyType, previousEntry?.otherSpecifyType) ?? "")
            : "",
        documentName: pickNonEmpty(typedEntry.documentName, previousEntry?.documentName) ?? "",
        issueDate: pickDateOrNull(typedEntry.issueDate, previousEntry?.issueDate),
        expiryDate: pickDateOrNull(typedEntry.expiryDate, previousEntry?.expiryDate),
        documentSummary:
          pickNonEmpty(typedEntry.documentSummary, previousEntry?.documentSummary) ?? "",
      };
    });
  }, [certificateType, fieldName, formData]);

  const removePendingFile = useCallback(
    (fileToRemove: File) => {
      updateStoreFiles((prev) =>
        prev.filter((entry) => !(entry instanceof File && entry === fileToRemove)),
      );
      const updatedEntries = buildFormItemsFromStore();
      persistFormEntries(updatedEntries);
      formData.clearErrors(fieldName);
    },
    [buildFormItemsFromStore, fieldName, formData, persistFormEntries, updateStoreFiles],
  );

  const uploadSingleFile = useCallback(
    async (params: { file: File; targetIdx: number; currentFormEntries: unknown }) => {
      const { file, targetIdx, currentFormEntries } = params;

      const existingUrlAtTargetIdx =
        Array.isArray(currentFormEntries) &&
        Boolean((currentFormEntries[targetIdx] as UploadItem | undefined)?.url);

      if (existingUrlAtTargetIdx) {
        return;
      }

      const payloadForSasTokenActionWrite = {
        prefix: SasTokenPrefix.MOVEMENT_SERVICE,
        contentType: file.type?.trim() || DEFAULT_UPLOAD_CONTENT_TYPE,
        privilege: SasTokenPrivilege.WRITE,
        blobUrl: null,
      };

      try {
        await runTransaction(
          {
            module: "vessel_call",
            screen: "MovementServiceDocumentUpload",
            action: "upload_movement_service_document",
            entityType: "movement_service_document",
          },
          async (transactionId) => {
            let sasTokenResponse: Awaited<ReturnType<typeof getSasToken>>;
            try {
              sasTokenResponse = await getSasToken(payloadForSasTokenActionWrite, transactionId);
            } catch (error) {
              throw error instanceof Error
                ? error
                : new Error("Failed to obtain SAS token from API.");
            }

            await uploadDocumentsToAzure.mutateAsync({
              file,
              blobUrl: sasTokenResponse.blobUrl,
              sasToken: sasTokenResponse.sasToken,
              certificateType,
              storeKey: fieldName,
              idx: targetIdx,
            });
          },
        );
      } catch (error) {
        const normalizedError =
          error instanceof Error ? error : new Error("Failed to upload document to Azure.");
        handleUploadError(normalizedError);
        removePendingFile(file);
      }
    },
    [certificateType, fieldName, handleUploadError, removePendingFile, uploadDocumentsToAzure],
  );

  const handleFieldChangeForAzure = useCallback(
    async (_fieldName: string, filesToUpload: File[]) => {
      const incomingFiles = Array.isArray(filesToUpload)
        ? filesToUpload.filter((entry): entry is File => entry instanceof File)
        : [];
      const formEntries = buildFormItemsFromStore();
      const storeEntries =
        useUploadStore.getState().getFilesForKey(fieldName) ?? EMPTY_UPLOAD_ENTRIES;
      const currentFormEntries = formData.getValues(fieldName);

      formData.setValue(fieldName, formEntries, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });

      if (incomingFiles.length === 0) return;

      setUploadsInFlight((count) => count + 1);

      try {
        for (const [fallbackIdx, file] of incomingFiles.entries()) {
          const absoluteIdx = storeEntries.findIndex(
            (entry) => entry instanceof File && entry === file,
          );
          const targetIdx = absoluteIdx >= 0 ? absoluteIdx : fallbackIdx;

          await uploadSingleFile({
            file,
            targetIdx,
            currentFormEntries,
          });
        }
      } finally {
        setUploadsInFlight((count) => Math.max(0, count - 1));
      }
    },
    [buildFormItemsFromStore, fieldName, formData, uploadSingleFile],
  );

  const removeFileAtIndex = useCallback(
    (index: number) => {
      const currentState = formData.getValues(fieldName);
      const updatedState = Array.isArray(currentState)
        ? currentState.filter((_, i: number) => i !== index)
        : [];

      persistFormEntries(updatedState);
      updateStoreFiles(updatedState);
      formData.clearErrors(fieldName);
    },
    [fieldName, formData, persistFormEntries, updateStoreFiles],
  );

  return {
    files,
    isLoading: uploadsInFlight > 0 || uploadDocumentsToAzure.isPending,
    setFiles: updateStoreFiles,
    handleFieldChangeForAzure,
    removeFileAtIndex,
  };
};
